import React from 'react';
import { Container, Paper, Typography, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Link } from 'react-router-dom';
import AssignmentReturn from '@material-ui/icons/AssignmentReturn';
import Security from '@material-ui/icons/Security';
import VerifiedUser from '@material-ui/icons/VerifiedUser';
import AccountBalance from '@material-ui/icons/AccountBalance';
import ContactMail from '@material-ui/icons/ContactMail';
import './Terms.css';

// Styles aligned with Terms / Privacy legal pages
const useStyles = makeStyles(() => ({
  paper: {
    background: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  typography: {
    color: '#adb5bd',
  },
  heading: {
    color: '#fff',
  },
  content: {
    color: '#adb5bd',
  },
}));

// Refund & Return Policy static page for paid digital video export
const Refund = () => {
  const classes = useStyles();

  return (
    <div className="terms-page">
      <div className="terms-hero">
        <AssignmentReturn className="terms-hero-icon" />
        <h1>Refund &amp; Return Policy</h1>
        <p>LED Scroller — Paid Video Export (digital service)</p>
      </div>

      <Container maxWidth="lg" className="terms-container">
        <Paper elevation={3} className={`terms-card full-width ${classes.paper}`}>
          <Box className="terms-card-header">
            <Security className="terms-card-icon" />
            <Typography variant="h5" className={classes.heading}>
              1. Nature of the service
            </Typography>
          </Box>
          <Box className="terms-card-content">
            <Typography paragraph className={classes.typography}>
              LED Scroller offers a paid <strong>digital video export</strong> (one-time unlock to download
              your LED scrolling animation). This is a <strong>web / digital service</strong>. There is no
              physical product and no shipping. Once payment succeeds and unlock is available, the service
              is generally <strong>non-returnable</strong>.
            </Typography>
            <Typography className={classes.typography}>
              Last updated: 6 September 2026. Related:{' '}
              <Link to="/terms">Terms</Link>, <Link to="/privacy-policy">Privacy Policy</Link>.
            </Typography>
          </Box>
        </Paper>

        <Paper elevation={3} className={`terms-card full-width ${classes.paper}`}>
          <Box className="terms-card-header">
            <VerifiedUser className="terms-card-icon" />
            <Typography variant="h5" className={classes.heading}>
              2. What we do if you paid but could not download
            </Typography>
          </Box>
          <Box className="terms-card-content">
            <Typography paragraph className={classes.typography}>
              If you completed payment but did not save your video (browser closed, device change, etc.),
              we treat this as a <strong>support / re-download</strong> case first — not an automatic refund.
            </Typography>
            <Typography component="div" className={classes.typography}>
              <ol>
                <li>
                  Keep your <strong>Order ID</strong> and <strong>Guest Access Code</strong> from the
                  success screen (copy or screenshot them).
                </li>
                <li>
                  Use on-site <strong>Recover download</strong> with Order ID + Access Code to unlock
                  download again (within our re-download limits).
                </li>
                <li>
                  If that fails, email <strong>usercontactcare@gmail.com</strong> with your Order ID
                  and/or Lemon Squeezy payment receipt.
                </li>
                <li>
                  After we verify payment, we will help you download again. In rare cases we may issue a
                  temporary unlock link (including by email when that feature is available).
                </li>
              </ol>
            </Typography>
          </Box>
        </Paper>

        <Paper elevation={3} className={`terms-card full-width ${classes.paper}`}>
          <Box className="terms-card-header">
            <AccountBalance className="terms-card-icon" />
            <Typography variant="h5" className={classes.heading}>
              3. When refunds are not available
            </Typography>
          </Box>
          <Box className="terms-card-content">
            <Typography paragraph className={classes.typography}>
              Because this is a digital / web service, we do <strong>not</strong> offer returns or refunds
              in the normal course, including when:
            </Typography>
            <Typography component="div" className={classes.typography}>
              <ul>
                <li>You successfully unlocked or downloaded the video;</li>
                <li>You changed your mind after payment;</li>
                <li>
                  You lost Order ID / Access Code without using Recover or contacting us with a valid
                  payment receipt;
                </li>
                <li>
                  Device, browser, or local issues occur after unlock was correctly issued (we still offer
                  re-download within policy).
                </li>
              </ul>
            </Typography>
          </Box>
        </Paper>

        <Paper elevation={3} className={`terms-card full-width ${classes.paper}`}>
          <Box className="terms-card-header">
            <AssignmentReturn className="terms-card-icon" />
            <Typography variant="h5" className={classes.heading}>
              4. Rare refund cases (genuine failure only)
            </Typography>
          </Box>
          <Box className="terms-card-content">
            <Typography paragraph className={classes.typography}>
              Refunds are considered only in <strong>rare, genuine</strong> cases after investigation, for
              example:
            </Typography>
            <Typography component="div" className={classes.typography}>
              <ul>
                <li>
                  You were charged but our systems never marked the order paid and unlock remains
                  impossible after support retries; or
                </li>
                <li>
                  A documented service defect prevented download despite valid payment and completed
                  support steps.
                </li>
              </ul>
            </Typography>
            <Typography className={classes.typography}>
              Even then, refund is at our discretion after we verify the facts — not a default “no
              questions asked” return for digital goods. There are <strong>no physical returns</strong>.
            </Typography>
          </Box>
        </Paper>

        <Paper elevation={3} className={`terms-card full-width ${classes.paper}`}>
          <Box className="terms-card-header">
            <ContactMail className="terms-card-icon" />
            <Typography variant="h5" className={classes.heading}>
              5. Contact
            </Typography>
          </Box>
          <Box className="terms-card-content">
            <Typography paragraph className={classes.typography}>
              For refund or re-download requests, contact:
            </Typography>
            <Typography className={classes.content}>
              Email: usercontactcare@gmail.com
            </Typography>
            <Typography className={classes.content}>
              Please include Order ID and/or payment receipt. Payments are processed by Lemon Squeezy
              (Merchant of Record); card data is not stored on ledscroller.com servers.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </div>
  );
};

export default Refund;
